import React, { FC } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { Form, Formik, FormikHelpers, FormikValues } from "formik";
import * as yup from "yup";
import { InputPanel } from "../../../components/Brief/InputPanel";
import { Button } from "../../../components/Button/Button";
import { editProject } from "../../../api/endpoints";
import axiosInstant from "../../../api";
import { BrandProject } from "./constants";
import { useUser } from "../../../hooks/useUser";
import { isInternal } from "../../../utils";

interface Props {
    project: BrandProject;
    onClose: () => void;
    onComplete: () => void;
}

const formSchema = yup.object().shape({
    tracking: yup.string().required(),
    // expert_tracking is only ever rendered for internal users; treat as
    // optional at the schema level so non-internal submits don't fail.
    expert_tracking: yup.string().notRequired()
});

export const ShippingDialog: FC<Props> = ({ project, onClose, onComplete }) => {
    const { tracking, expert_tracking, brand_id: brandId, id } = project;
    const { role } = useUser();
    const internalUser = isInternal(role);
    const initValues = {
        tracking: tracking || "",
        expert_tracking: expert_tracking || ""
    };

    const updateTrackingNo = async (data: FormikValues, { setSubmitting }: FormikHelpers<typeof initValues>) => {
        setSubmitting(true);

        try {
            // Strip expert_tracking from the payload entirely for non-internal
            // users so the field never round-trips even if React state had a stale
            // value from a previous internal session.
            const payload: any = { ...data };
            if (!internalUser) {
                delete payload.expert_tracking;
            }
            await axiosInstant.patch(editProject(brandId, id), payload);
            toast.success("Shipping address updated successfully.");
            setSubmitting(false);
            onComplete();
        } catch (err) {
            setSubmitting(false);
            toast.error((err as Error)?.message);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h5">Product Shipment</Typography>
            </DialogTitle>
            <Formik initialValues={initValues} validationSchema={formSchema} onSubmit={updateTrackingNo}>
                {({ isSubmitting }) => (
                    <DialogContent className="border-y border-border-token">
                        {project.can_ship_two ? (
                            <Box className="py-6 flex flex-col w-full gap-6">
                                <Typography className="text-sm text-text-2">
                                    Please ship two products to the following address:
                                </Typography>
                                <Box className="p-4 bg-surface-2 border border-border-token rounded-2xl w-[264px] max-sm:w-full">
                                    <Typography className="text-sm text-text-2 w-[170px] max-sm:w-full">
                                        The Desire Company Attn: Katelyn Henslin 4245 N Knox Ave Chicago, IL 60641
                                        Phone: 612-267-8352
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box className="py-6 flex flex-col w-full gap-6">
                                <Typography className="text-sm text-text-2">
                                    Shipping is being handled directly with Account Management
                                </Typography>
                            </Box>
                        )}

                        {project.can_ship_two ? (
                            <Form>
                                <InputPanel name="tracking" body="Tracking number" />
                                {internalUser && <InputPanel name="expert_tracking" body="Expert tracking number" />}
                                <DialogActions className="!px-6 !py-4">
                                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                                        {isSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </DialogActions>
                            </Form>
                        ) : (
                            <DialogActions>
                                <Button
                                    color="default"
                                    variant="soft"
                                    size="md"
                                    className="!font-normal !min-w-min !py-2 !px-5 hover:!bg-surface-2"
                                    onClick={onClose}
                                >
                                    Close
                                </Button>
                            </DialogActions>
                        )}
                    </DialogContent>
                )}
            </Formik>
        </Dialog>
    );
};
